<script setup>
import { computed, watch, ref } from 'vue'
import { useStore } from 'vuex'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import Button from '@/components/ui/Button.vue'
import Avatar from '@/components/ui/Avatar.vue'
import ThemeToggle from '@/components/ui/ThemeToggle.vue'

const store = useStore()
const router = useRouter()
const route = useRoute()
const { locale } = useI18n()

const user = computed(() => store.state.user)
const companyName = process.env.VUE_APP_COMPANY_NAME || ''
const mobileMenuOpen = ref(false)

watch(locale, (newLang) => {
  localStorage.setItem('lang', newLang)
})
watch(route, () => { mobileMenuOpen.value = false })

const toggleLanguage = () => {
  locale.value = locale.value === 'en' ? 'me' : 'en'
}

const handleLogout = async () => {
  await store.dispatch('logout')
  router.push('/login')
}

const handleMobileLogout = () => {
  mobileMenuOpen.value = false
  handleLogout()
}

// Split pages: login, /, and unlock routes
const isSplitPage = computed(() => {
  return route.path === '/login' || route.path === '/' || route.path.startsWith('/unlock/')
})
</script>

<template>
  <div id="app" class="h-screen flex flex-col bg-background overflow-y-auto">
    <header
      class="relative z-50 flex-shrink-0"
      :class="[
        isSplitPage
          ? 'header-split absolute w-full top-0 left-0'
          : 'navbar-glass'
      ]"
    >
      <div class="flex h-10 items-center justify-between pt-3">
        <!-- Left: title -->
        <div
          class="w-1/2 flex items-center px-4"
          :class="isSplitPage ? 'left-header-text' : ''"
        >
          <h1 class="font-semibold text-sm">{{ companyName || $t('nav.title') }}</h1>
        </div>

        <!-- Right: controls -->
        <div
          class="w-1/2 flex items-center justify-end px-4 gap-3"
          :class="isSplitPage ? 'right-header-text' : ''"
        >
          <!-- Desktop controls -->
          <div class="hidden md:flex items-center gap-3">
            <template v-if="user">
              <span class="hidden text-sm text-muted lg:inline">
                {{ $t('nav.hi', { name: user.name }) }}
              </span>
              <Avatar
                :size="28"
                class="ring-1 ring-black/10 dark:ring-white/15"
                :fallback="user?.name?.[0]?.toUpperCase() || 'U'"
              />
              <Button variant="outline" @click="handleLogout" class="btn-pill">
                {{ $t('nav.logout') }}
              </Button>
            </template>
            <button
              @click="toggleLanguage"
              class="navbar-select w-12 text-center"
              aria-label="Promena jezika"
            >
              {{ locale.toUpperCase() }}
            </button>
            <ThemeToggle />
          </div>

          <!-- Mobile hamburger button -->
          <button
            @click="mobileMenuOpen = !mobileMenuOpen"
            class="md:hidden relative z-50 w-8 h-8 flex flex-col items-center justify-center gap-[5px]"
            aria-label="Menu"
          >
            <span class="block w-5 h-0.5 bg-current rounded transition-all duration-200"
              :class="mobileMenuOpen ? 'rotate-45 translate-y-[7px]' : ''" />
            <span class="block w-5 h-0.5 bg-current rounded transition-all duration-200"
              :class="mobileMenuOpen ? 'opacity-0 scale-x-0' : ''" />
            <span class="block w-5 h-0.5 bg-current rounded transition-all duration-200"
              :class="mobileMenuOpen ? '-rotate-45 -translate-y-[7px]' : ''" />
          </button>
        </div>
      </div>

      <!-- Mobile dropdown menu -->
      <transition name="menu-drop">
        <div
          v-if="mobileMenuOpen"
          class="md:hidden absolute right-3 top-11 w-52 rounded-xl shadow-2xl border z-50 overflow-hidden bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700"
        >
          <template v-if="user">
            <div class="px-4 py-3 border-b border-slate-100 dark:border-slate-800">
              <p class="text-sm font-semibold text-slate-900 dark:text-white truncate">{{ $t('nav.hi', { name: user.name }) }}</p>
            </div>
            <button
              type="button"
              @click="handleMobileLogout"
              class="w-full text-left px-4 py-3 text-sm text-slate-700 dark:text-slate-200 hover:bg-black/5 dark:hover:bg-white/5 transition-colors flex items-center gap-2"
            >
              <svg class="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              {{ $t('nav.logout') }}
            </button>
            <div class="border-t border-slate-100 dark:border-slate-800" />
          </template>

          <div class="flex items-center justify-between px-4 py-3">
            <span class="text-sm text-slate-700 dark:text-slate-200">Jezik / Language</span>
            <button
              type="button"
              @click="toggleLanguage"
              class="navbar-select w-14 text-center text-sm"
              aria-label="Promena jezika"
            >
              {{ locale.toUpperCase() }}
            </button>
          </div>

          <div class="flex items-center justify-between px-4 py-3">
            <span class="text-sm text-slate-700 dark:text-slate-200">Tema / Theme</span>
            <ThemeToggle />
          </div>
        </div>
      </transition>
    </header>

    <main
      class="flex-1"
      :class="isSplitPage ? '' : 'container py-8'"
    >
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <footer v-if="!isSplitPage" class="container py-8 text-center text-sm text-muted flex-shrink-0">
      {{ $t('footer.rights', { year: new Date().getFullYear() }) }}
    </footer>
  </div>
</template>

<style>
body {
  margin: 0;
}

/* Split header: gradient that mirrors the two panels */
.header-split {
  /* Light mode: left panel is dark slate, right panel is white */
  background: linear-gradient(to right, #1e293b 50%, #ffffff 50%);
  border-bottom: none;
  box-shadow: none;
}
.dark .header-split {
  /* Dark mode: left panel is white, right panel is dark navy */
  background: linear-gradient(to right, #ffffff 50%, #0f172a 50%);
}

/* Text colors for split header left side */
.left-header-text {
  /* Light mode: left is dark, so text is white */
  color: #ffffff;
}
.dark .left-header-text {
  /* Dark mode: left is white, so text is dark */
  color: #0f172a;
}

/* Text colors for split header right side */
.right-header-text {
  /* Light mode: right is white, so text is dark */
  color: #0f172a;
}
.dark .right-header-text {
  /* Dark mode: right is dark, so text is white */
  color: #ffffff;
}

/* Mobile: single-panel header matching the right (action) panel */
@media (max-width: 767px) {
  .header-split {
    background: #ffffff;
  }
  .dark .header-split {
    background: #0f172a;
  }
  .left-header-text {
    color: #0f172a;
  }
  .dark .left-header-text {
    color: #ffffff;
  }
}

/* Dark scrollbar */
.dark #app {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}
.dark #app::-webkit-scrollbar {
  width: 8px;
}
.dark #app::-webkit-scrollbar-track {
  background: transparent;
}
.dark #app::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
}
.dark #app::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.menu-drop-enter-active,
.menu-drop-leave-active {
  transition: all 0.15s ease;
}
.menu-drop-enter-from,
.menu-drop-leave-to {
  opacity: 0;
  transform: translateY(-6px) scale(0.97);
}

.navbar-select {
  background-color: rgba(0, 0, 0, 0.05);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: inherit;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;
}
.dark .navbar-select {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.2);
}
.navbar-select:hover {
  background-color: rgba(0, 0, 0, 0.08);
}
.dark .navbar-select:hover {
  background-color: rgba(255, 255, 255, 0.2);
}
.navbar-select:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.12);
}
.dark .navbar-select:focus {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
}
.navbar-select option {
  background-color: white;
  color: black;
}
.dark .navbar-select option {
  background-color: #2a2a2e;
  color: white;
}
</style>
